/**
 * Backend team API — list + invite hooks.
 *
 * `useTeam()` wraps GET /v1/team. Visible to admin / super_admin /
 * compliance / rm / director roles. Email is full for admin tier and
 * redacted (`a…@firma.co.za`) for the rest.
 *
 * `useInviteTeamMember()` wraps POST /v1/team/invite. Returns 202 with
 * `supabaseStepRequired: true` — the Cosmos skeleton lands here, the
 * Supabase magic-link side fires from tenant-onboarding FA when wired.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
import { useStepUpQueue, wasReplayed, type AnyMutation } from './step-up-store';

export type TeamMemberRole =
  | 'user'
  | 'admin'
  | 'rm'
  | 'compliance'
  | 'director'
  | 'super_admin';

export type TeamMemberLifeStatus =
  | 'active'
  | 'invited'
  | 'deceased'
  | 'incapacitated'
  | 'suspended';

export interface TeamMemberView {
  id: string;
  tenantId: string;
  email: string;
  displayName: string;
  role: TeamMemberRole;
  /** Which of the firm's own role NAMES this member holds — the `id` of a
   *  `CustomRoleView` from `/v1/tenants/me`. Resolve it for DISPLAY only:
   *  `role` above is the canonical token every authz check reads, and it always
   *  holds the alias's base. An id that no longer resolves (the firm deleted
   *  that role) falls back to the firm's role title, then to the Elreign name —
   *  see `resolveRoleDisplay` in lib/labels. */
  roleLabelId?: string;
  trustIds: string[];
  lifeStatus?: TeamMemberLifeStatus;
  /** ISO timestamp of the last invitation EMAIL sent (first send or resend).
   *  Absent on invites created before 2026-08-11 — treat absent as UNKNOWN,
   *  never as expired. */
  inviteSentAt?: string;
  lifecycleEventAt?: string;
  /** H-09: designated MLRO. Surfaces as a "MLRO" badge in admin/team. */
  isMlro?: boolean;
  mlroDelegateOf?: string;
  /** Lane C · C1 (2026-07-09): the member's SUPABASE user id — the id the
   *  RM-assignment endpoint (`POST /v1/firm/clients/{id}/assignees`) and every
   *  RM-scoping surface key on. Present for ADMIN-tier viewers only (backend
   *  gates it behind the same PII gate as full email); absent for
   *  invited/skeleton members with no Supabase account yet. The Client Book's
   *  assignee picker sends these — NOT the Cosmos `id`. */
  supabaseUserId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TeamListResponse {
  members: TeamMemberView[];
  correlationId: string;
}

export function useTeam(opts: { enabled?: boolean } = {}) {
  return useQuery<TeamListResponse>({
    queryKey: ['team', 'list'],
    queryFn: ({ signal }) =>
      api<TeamListResponse>('/api/v1/team', { signal }),
    enabled: opts.enabled ?? true,
  });
}

/** 'user' = a PORTAL CLIENT (a trust's trustee/settlor human), invited from
 *  the trust's client-detail screen with a REQUIRED assignedClientIds trust
 *  scope (2026-06-11 client-invite feature). The Team screen's staff dropdown
 *  deliberately does NOT offer it — clients are not staff. */
export type InviteRole = 'admin' | 'rm' | 'compliance' | 'director' | 'user';

export interface InviteTeamMemberRequest {
  email: string;
  /** THE PERMISSION. Always one of the canonical tokens — when the inviter
   *  picks one of the firm's own role names, this carries that alias's `base`,
   *  never the alias itself. Nothing downstream of here (least of all
   *  `app_metadata.role`) knows the custom role exists. */
  role: InviteRole;
  /** THE WORD. The `id` of one of the firm's `customRoles`, sent alongside —
   *  and only alongside — its base in `role` above. Display-only; the backend
   *  shape-checks it and stores it as a pointer, and refuses it entirely for
   *  `role: 'user'` (a portal client holds no chair in the firm). */
  roleLabelId?: string;
  displayName?: string;
  /**
   * FO-1 (locked UX spec §6.3): scope a relationship-manager to specific
   * client trusts at invite time. Only meaningful for the `rm` role — the
   * backend ignores it for tenant-wide roles (admin/compliance/director see
   * the whole book). Omit or send an empty array for tenant-wide scope.
   * Each id must be a real trust in the caller's tenant (the backend 400s
   * otherwise). Recorded on the invited UserDoc as `trustIds`.
   */
  assignedClientIds?: string[];
  /**
   * Client-invite tier (2026-07-02 multi-member end-client access, Task B3).
   * Only meaningful for `role: 'user'` (a portal client) — the backend
   * ignores it for staff roles and defaults to 'trustee' when omitted.
   * 'trustee' can act on the trust; 'beneficiary' is view-only.
   */
  tier?: 'trustee' | 'beneficiary';
}

export interface InviteTeamMemberResponse {
  user: TeamMemberView;
  inviteSent: boolean;
  supabaseStepRequired: boolean;
  correlationId: string;
}

export function useInviteTeamMember() {
  const qc = useQueryClient();
  return useMutation<InviteTeamMemberResponse, ApiError, InviteTeamMemberRequest>({
    mutationFn: (body) =>
      api<InviteTeamMemberResponse>('/api/v1/team/invite', {
        method: 'POST',
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

export interface InviteSendResponse {
  inviteSent: boolean;
  /** 2026-08-06: the invitee ALREADY had an Elreign login, so there was no
   *  set-a-password link to send. The backend linked their existing account to
   *  this workspace instead. See docs-vault/10-identity/.
   *
   *  @deprecated 2026-08-23 — the backend now SENDS an existing-account
   *  sign-in notification rather than reporting a non-send, so this flag no
   *  longer appears. `existingAccount` replaces it and comes alongside
   *  `inviteSent: true`. Kept only so an older cached bundle keeps type-checking. */
  alreadyRegistered?: boolean;
  /** The address already had an Elreign login. GoTrue refuses `/invite` for a
   *  registered address and sends nothing, so they were emailed a SIGN-IN
   *  notification instead of a set-a-password invitation. This is not a
   *  failure — read `inviteSent` for whether the email actually went. */
  existingAccount?: boolean;
  /** True when the account was attached to this workspace (with or without an
   *  email going out). This is the outcome that actually matters. */
  linked?: boolean;
  /** A notification for this invitation had already gone out, so this one is a
   *  re-send with a freshly minted link (the previous link is superseded).
   *  Never a conflict — repeat sends are a supported, normal action. */
  resent?: boolean;
  message?: string;
  noticeFlags?: {
    supabaseAdminSkipped?: boolean;
    /** No email provider configured in this environment — the message was only
     *  logged. Always paired with `inviteSent: false`. */
    emailProviderUnconfigured?: boolean;
    /** The one-click sign-in link could not be minted, so the email points at
     *  the plain sign-in page. The email DID go out. */
    signInLinkUnavailable?: boolean;
  };
  correlationId: string;
}

/**
 * Second half of the team-invite chain — fires the Supabase magic-link
 * via the tenant-onboarding FA. Call this after useInviteTeamMember()
 * returns a Cosmos UserDoc id.
 */
export function useSendTeamInvite() {
  const qc = useQueryClient();
  return useMutation<InviteSendResponse, ApiError, { userId: string }>({
    mutationFn: ({ userId }) =>
      api<InviteSendResponse>(
        `/api/v1/admin/team/${encodeURIComponent(userId)}/invite-send`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

// ── MLRO designation (F-033, FIC s43B) ──────────────────────────────────────
//
// PATCH /v1/admin/users/{userId}/mlro-designation. Body mirrors
// `MlroDesignateSchema` (`.strict()`) in
// `backend/src/handlers/admin-users-mlro-designate.ts`:
//   { isMlro: boolean, mlroDelegateOf?: string | null }
//
// super_admin only + step-up gated server-side. Multiple users CAN be
// isMlro=true simultaneously (the four-eye rule on FIC submission only
// needs one valid MLRO) — this is a toggle, not a single-owner slot.
// `mlroDelegateOf` is independent of `isMlro`; send `null` to clear it.

export interface MlroDesignateInput {
  /** Cosmos UserDoc id of the team member being (un)designated. */
  userId: string;
  isMlro: boolean;
  /** Set to the principal MLRO's userId to mark this user as their delegate,
   *  or `null` to clear an existing delegate link. Omit to leave unchanged. */
  mlroDelegateOf?: string | null;
}

export interface MlroDesignateResponse {
  user: {
    id: string;
    tenantId: string;
    isMlro?: boolean;
    mlroDelegateOf?: string;
    displayName: string;
    role: TeamMemberRole;
  };
  correlationId: string;
}

export function useDesignateMlro() {
  const qc = useQueryClient();
  return useMutation<MlroDesignateResponse, ApiError, MlroDesignateInput>({
    mutationFn: ({ userId, ...body }) =>
      api<MlroDesignateResponse>(
        `/api/v1/admin/users/${encodeURIComponent(userId)}/mlro-designation`,
        { method: 'PATCH', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

export interface PortalInviteHandlers {
  /** The magic-link email leg resolved. `res.inviteSent` distinguishes a real
   *  send from a saved-but-not-delivered case. */
  onSent?: (res: InviteSendResponse, userId: string) => void;
  /** The SEND leg failed (network / server). The Cosmos skeleton already
   *  exists — the member is on file but was not emailed. */
  onSendError?: (err: ApiError, userId: string) => void;
}

/**
 * Replay-safe two-leg portal invite: CREATE the Cosmos skeleton + trust
 * membership (POST /v1/team/invite, api FA) then SEND the Supabase magic-link
 * (POST /v1/admin/team/{id}/invite-send, onboarding FA — it holds the
 * service-role key, hence the split).
 *
 * WHY THIS HOOK EXISTS — the naive call-site chain
 *   invite.mutate(vars, { onSuccess: () => send.mutate({ userId }) })
 * SILENTLY DROPS the send leg whenever step-up fires (i.e. any invite where the
 * caller hadn't stepped up in the last ~10 min — the common case). Both legs
 * are step-up-gated; the CREATE leg 403s STEP_UP_REQUIRED, and the global
 * StepUpInterceptor replays it through the MUTATION CACHE. That replay updates
 * this observer's isSuccess/data via cache subscription, but does NOT re-invoke
 * the per-.mutate() call-level onSuccess callback (that callback was bound to
 * the original mutate() promise, which already rejected). Net effect: the
 * skeleton is created but no email is ever sent, on every un-stepped-up invite.
 *
 * FIX — drive the SEND leg off the CREATE success STATE with an effect. The
 * replay's subscription update flips `invite.isSuccess`, so the send fires
 * exactly once per created userId whether the create succeeded directly or via
 * a step-up replay. The send's own step-up (rare — the create just satisfied
 * it) is handled by the interceptor replaying the SEND mutation; its result
 * still resolves the state the caller renders from.
 *
 * The caller keeps calling `invite.mutate(vars, { onError })` itself (screens
 * differ on how they collect vars + render create errors); this hook owns only
 * the fragile create→send handoff.
 */
/**
 * Upper bound on waiting for a step-up replay to settle. Generous because the
 * user has to find their authenticator and type a code — minutes, not seconds.
 * Documented fallback: if neither a replay outcome nor a modal dismissal is
 * ever observed (e.g. the mutation was garbage-collected mid-challenge), the
 * `mutateReplaySafe` promise rejects with the original STEP_UP_REQUIRED error
 * instead of hanging a sequential caller forever.
 */
export const REPLAY_SAFE_TIMEOUT_MS = 120_000;

export interface MutateReplaySafeOptions {
  /** Override the bounded wait (tests only). Defaults to REPLAY_SAFE_TIMEOUT_MS. */
  timeoutMs?: number;
}

/**
 * Imperative replay-safe mutate — the `await`-shaped fix for the SAME step-up
 * bug class `usePortalInvite` solves reactively.
 *
 * THE BUG CLASS — for any step-up-gated mutation, `mutateAsync(vars)` REJECTS
 * on the first STEP_UP_REQUIRED 403. The global StepUpInterceptor then queues
 * the failed cache Mutation and, after the user passes the MFA challenge, the
 * StepUpModalHost replays it via `mutation.execute(variables)`. That replay
 * updates the observer STATE (isSuccess/data) but can never resolve the
 * original `mutateAsync` promise (it already rejected) and never re-fires
 * call-level callbacks. Any `await`-chain built on the naked promise therefore
 * silently drops everything downstream of the challenged leg — e.g. the firm
 * onboarding team step created the teammate but never fired the email SEND.
 *
 * THIS HELPER returns a promise that reflects the mutation's TERMINAL state
 * across replays:
 *   - resolves with the success data whether it arrives directly or via a
 *     step-up replay (`execute` through the mutation cache);
 *   - rejects on a terminal non-step-up error (first attempt or replay);
 *   - rejects with the original challenge error when the user DISMISSES the
 *     step-up modal (`useStepUpQueue.dismiss` empties the queue and the
 *     mutation stays in error state forever — without this branch a caller
 *     would hang) or when the replay fails STEP_UP_REQUIRED again (the
 *     interceptor's `wasReplayed` guard makes that terminal too);
 *   - rejects with the original challenge error after `timeoutMs` as a
 *     bounded last-resort fallback (see REPLAY_SAFE_TIMEOUT_MS).
 *
 * Drain-vs-dismiss detection: both `drain()` (replay imminent) and `dismiss()`
 * empty `pending` and close the modal — indistinguishable from the store
 * transition alone. But `StepUpModalHost.replayQueue` calls `markReplayed` on
 * the first drained mutation SYNCHRONOUSLY after `drain()`, while `dismiss`
 * marks none — so one macrotask later, `wasReplayed` on the drained batch
 * tells the two apart deterministically.
 */
export async function mutateReplaySafe<TData, TVars>(
  qc: QueryClient,
  mutation: { mutateAsync: (vars: TVars) => Promise<TData> },
  vars: TVars,
  opts: MutateReplaySafeOptions = {},
): Promise<TData> {
  try {
    return await mutation.mutateAsync(vars);
  } catch (err) {
    if (!(err instanceof ApiError) || !err.isStepUpRequired) throw err;
    // Locate the cache-level Mutation that just rejected with THIS error —
    // mutateAsync rejects with the exact object stored in `state.error`, so
    // object identity is a precise join key (no variables-shape guessing).
    const failed = qc
      .getMutationCache()
      .getAll()
      .find((m) => m.state.error === err) as AnyMutation | undefined;
    // Untrackable (already gc'd from the cache) or this WAS the replay and it
    // failed step-up again — the interceptor treats that as terminal (no
    // re-queue), so surface the error rather than wait for a second replay
    // that will never come.
    if (!failed || wasReplayed(failed)) throw err;
    return awaitReplayOutcome<TData>(qc, failed, err, opts.timeoutMs ?? REPLAY_SAFE_TIMEOUT_MS);
  }
}

/** Wait for a step-up-challenged mutation to reach a terminal outcome via the
 *  interceptor + modal replay machinery. See mutateReplaySafe for semantics. */
function awaitReplayOutcome<TData>(
  qc: QueryClient,
  m: AnyMutation,
  challengeError: ApiError,
  timeoutMs: number,
): Promise<TData> {
  return new Promise<TData>((resolve, reject) => {
    const cleanups: Array<() => void> = [];
    let settled = false;
    function settle(fn: () => void) {
      if (settled) return;
      settled = true;
      for (const c of cleanups) c();
      fn();
    }

    // Outcomes 1 + 2: the replay settles the mutation in the cache.
    const unsubCache = qc.getMutationCache().subscribe((event) => {
      if (event.mutation !== m) return;
      if (event.type === 'removed') {
        // Gc'd mid-challenge — no replay outcome will ever arrive.
        settle(() => reject(challengeError));
        return;
      }
      if (event.type !== 'updated') return;
      if (m.state.status === 'success') {
        settle(() => resolve(m.state.data as TData));
      } else if (m.state.status === 'error') {
        settle(() => reject(m.state.error));
      }
    });
    cleanups.push(unsubCache);

    // Outcome 3: our mutation LEFT the step-up queue. Drain and dismiss look
    // identical in the store transition; one macrotask later `wasReplayed` on
    // the departed batch distinguishes them (see mutateReplaySafe doc).
    const unsubQueue = useStepUpQueue.subscribe((state, prev) => {
      const wasQueued = prev.pending.some((p) => p.mutationId === m.mutationId);
      const stillQueued = state.pending.some((p) => p.mutationId === m.mutationId);
      if (!wasQueued || stillQueued) return;
      const batch = prev.pending;
      setTimeout(() => {
        if (settled) return;
        if (batch.some((p) => wasReplayed(p))) return; // drained — replay running
        settle(() => reject(challengeError)); // dismissed — terminal
      }, 0);
    });
    cleanups.push(unsubQueue);

    // Outcome 4 (documented last-resort): bounded wait.
    const timer = setTimeout(() => settle(() => reject(challengeError)), timeoutMs);
    cleanups.push(() => clearTimeout(timer));

    // Defensive: the replay could in principle settle between the rejection
    // and this subscription being attached.
    if (m.state.status === 'success') settle(() => resolve(m.state.data as TData));
  });
}

export function usePortalInvite(handlers: PortalInviteHandlers = {}) {
  const invite = useInviteTeamMember();
  const send = useSendTeamInvite();

  // Refs so the effect depends ONLY on the created userId — not on the freshly
  // allocated mutation/handler objects each render (which would re-run it).
  const sendRef = useRef(send);
  sendRef.current = send;
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const chained = useRef<Set<string>>(new Set());

  const createdUserId = invite.isSuccess ? invite.data?.user.id : undefined;

  useEffect(() => {
    if (!createdUserId || chained.current.has(createdUserId)) return;
    chained.current.add(createdUserId); // fire the send exactly once per user
    sendRef.current.mutate(
      { userId: createdUserId },
      {
        onSuccess: (res) => handlersRef.current.onSent?.(res, createdUserId),
        onError: (err) => handlersRef.current.onSendError?.(err, createdUserId),
      },
    );
  }, [createdUserId]);

  const reset = useCallback(() => {
    invite.reset();
    send.reset();
    // `chained` is deliberately NOT cleared: dedup is by created userId and a
    // fresh invite yields a new id, so clearing only risks a double-send if
    // invite.data lingers a render after reset.
  }, [invite, send]);

  return {
    /** The CREATE mutation — the caller drives `.mutate(vars, { onError })`. */
    invite,
    /** The SEND mutation — exposed for isPending / error rendering. */
    send,
    /** Reset both legs (call on modal close). */
    reset,
    /** True while EITHER leg is in flight. */
    isPending: invite.isPending || send.isPending,
  };
}

// ── Admin session revoke — "sign out everywhere" (lane C · C7, 2026-07-09) ──
//
// POST /v1/admin/users/{userId}/sessions/revoke
// (backend/src/functions/auth/admin-users-sessions-revoke.ts, tenant-onboarding
// FA — it holds the session-store seam). `userId` is the Cosmos UserDoc id (the
// team-list row id); the handler resolves the target's supabaseUserId and
// deletes every auth.sessions row, so the target's devices can't refresh.
// Step-up gated; admins may revoke others, any role may self-revoke.
//
// Honest semantics the UI must carry (per the handler):
//   - This is a sign-out, NOT a ban — the user can sign straight back in.
//   - Existing aal2 access tokens stay valid until their TTL lapses (Supabase
//     limitation; Sentinel alerts on revoked-token use).
//   - `sessionsRevoked: false` + `noticeFlags.supabaseAdminSkipped` = nothing
//     was revoked (target has no login yet, or the session store isn't wired
//     in this environment) — never render that as success.

export interface RevokeUserSessionsResponse {
  sessionsRevoked: boolean;
  revokedCount?: number;
  noticeFlags?: { supabaseAdminSkipped?: boolean };
  correlationId: string;
}

function buildSessionsRevokePath(userId: string): string {
  return `/api/v1/admin/users/${encodeURIComponent(userId)}/sessions/revoke`;
}

export function useRevokeUserSessions() {
  return useMutation<RevokeUserSessionsResponse, ApiError, { userId: string }>({
    mutationFn: ({ userId }) =>
      api<RevokeUserSessionsResponse>(buildSessionsRevokePath(userId), {
        method: 'POST',
      }),
  });
}

// ── Remove a staff member (2026-08-08) ──────────────────────────────────────
//
// `POST /v1/team/{userId}/remove` — mirrors
// `backend/src/functions/auth/team-remove.ts`. Until this shipped a firm could
// invite staff and never remove them.
//
// ⚠️ THIS IS NOT THE LIFECYCLE PAIR BELOW, and the difference matters when you
// choose which to offer. Death / incapacity BAN the Supabase login and sign it
// out everywhere, because the human is gone. Removal revokes access to THIS
// firm only, leaving the person's other workspaces alone — a trust officer who
// leaves your firm may hold their own personal account, or work at another firm
// on the same platform. Use lifecycle for "this person has died or lost
// capacity"; use removal for "this person no longer works here".
//
// Step-up gated server-side; the global StepUpInterceptor replays after a fresh
// MFA challenge. Single call — no fan-out, unlike invite and lifecycle.
//
// Refusals are 409 and each names a specific lockout the firm was about to
// inflict on itself: CANNOT_REMOVE_SELF, CANNOT_REMOVE_OWNER, LAST_ADMIN.
export interface RemoveTeamMemberResponse {
  removed: boolean;
  /** True when the member was already removed — a re-run, not a failure. */
  alreadyRemoved?: boolean;
  membershipRevoked: boolean;
  claimCleared: boolean;
  /** null when the session store is unreachable — see `sessionsUnavailable`. */
  sessionsRevoked: number | null;
  noticeFlags?: { supabaseAdminSkipped?: boolean; sessionsUnavailable?: boolean };
  correlationId?: string;
}

function buildTeamRemovePath(userId: string): string {
  return `/api/v1/team/${encodeURIComponent(userId)}/remove`;
}

export function useRemoveTeamMember() {
  const qc = useQueryClient();
  return useMutation<RemoveTeamMemberResponse, ApiError, { userId: string }>({
    mutationFn: ({ userId }) =>
      api<RemoveTeamMemberResponse>(buildTeamRemovePath(userId), { method: 'POST' }),
    // The removed person disappears from the list and their assignments change,
    // so the team read must not serve a stale roster showing them still active.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

// ── Death / incapacity lifecycle (F-008 / F-034) ────────────────────────────
//
// A two-Function-App-fan-out the FE has to drive explicitly:
//   1. INITIATE (main-api-audit FA) — flips the Cosmos UserDoc's lifeStatus
//      and stamps evidence. Body mirrors `LifecycleDeathSchema` /
//      `LifecycleIncapacitySchema` (`.strict()`) in
//      `backend/src/handlers/users-lifecycle-{death,incapacity}.ts`.
//      Returns 202 + `supabaseStepRequired: true` — the Cosmos side is done
//      but the Supabase auth-account side is NOT yet revoked.
//   2. EXECUTE (tenant-onboarding FA) — bans the Supabase user + revokes
//      sessions. No request body. Mirrors
//      `backend/src/functions/auth/admin-users-lifecycle-{death,incapacity}-execute.ts`.
//      409 LIFECYCLE_STATE_MISMATCH if step 1 hasn't landed yet (or was for
//      the other lifecycle kind) — the caller MUST run INITIATE first.
//      IDEMPOTENT — safe to call execute twice (e.g. a retry after a
//      network blip); Supabase ban/sign-out are idempotent per-user.
//
// Both legs are step-up gated server-side; the global StepUpInterceptor
// replays a 403 STEP_UP_REQUIRED after a fresh MFA challenge — callers
// should NOT chain step 2 off step 1's per-.mutate() onSuccess (same
// dropped-callback hazard `usePortalInvite`/`mutateReplaySafe` exist for).
// The team screen drives the two steps as separate, explicitly-confirmed
// user actions instead of an automatic fan-out, since EXECUTE is the
// irreversible half (bans the login) and deserves its own confirmation.

export type LifecycleKind = 'death' | 'incapacity';

export interface LifecycleInitiateInput {
  userId: string;
  kind: LifecycleKind;
  /** ISO date string. Required for both kinds — the wire field name differs
   *  per kind (`dateOfDeath` vs `dateOfIncapacity`), handled internally. */
  date: string;
  evidenceDocumentId?: string;
  notifiedBy?: string;
  /** Incapacity-only. Ignored (never sent) for kind: 'death'. */
  incapacityType?: 'medical' | 'legal' | 'other';
}

export interface LifecycleInitiateResponse {
  user: {
    id: string;
    tenantId: string;
    supabaseUserId: string;
    email: string;
    displayName: string;
    role: TeamMemberRole;
    trustIds: string[];
    lifeStatus?: TeamMemberLifeStatus;
    lifecycleEventAt?: string;
    lifecycleEvidenceDocumentId?: string;
    incapacityType?: 'medical' | 'legal' | 'other';
    createdAt: string;
    updatedAt: string;
  };
  supabaseStepRequired: true;
  correlationId: string;
}

function buildLifecyclePath(kind: LifecycleKind, userId: string): string {
  return `/api/v1/users/${encodeURIComponent(userId)}/lifecycle/${kind}`;
}

/** Step 1 — flip the Cosmos UserDoc's lifeStatus + stamp evidence. Admin /
 *  compliance / super_admin only; step-up required. */
export function useInitiateLifecycle() {
  const qc = useQueryClient();
  return useMutation<LifecycleInitiateResponse, ApiError, LifecycleInitiateInput>({
    mutationFn: ({ userId, kind, date, evidenceDocumentId, notifiedBy, incapacityType }) =>
      api<LifecycleInitiateResponse>(buildLifecyclePath(kind, userId), {
        method: 'POST',
        body: {
          ...(kind === 'death' ? { dateOfDeath: date } : { dateOfIncapacity: date }),
          ...(evidenceDocumentId ? { evidenceDocumentId } : {}),
          ...(notifiedBy ? { notifiedBy } : {}),
          ...(kind === 'incapacity' && incapacityType ? { incapacityType } : {}),
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}

export interface LifecycleExecuteInput {
  userId: string;
  kind: LifecycleKind;
}

export interface LifecycleExecuteResponse {
  supabaseBanned: boolean;
  sessionsRevoked: boolean;
  noticeFlags?: { supabaseAdminSkipped?: boolean };
  correlationId: string;
}

function buildLifecycleExecutePath(kind: LifecycleKind, userId: string): string {
  return `/api/v1/admin/users/${encodeURIComponent(userId)}/lifecycle/${kind}-execute`;
}

/** Step 2 — ban the Supabase auth user + revoke sessions. admin / compliance
 *  / super_admin; step-up required. Requires step 1 (INITIATE) to have
 *  already landed the matching lifeStatus, or the backend 409s
 *  LIFECYCLE_STATE_MISMATCH. Idempotent — safe to retry. */
export function useExecuteLifecycle() {
  const qc = useQueryClient();
  return useMutation<LifecycleExecuteResponse, ApiError, LifecycleExecuteInput>({
    mutationFn: ({ userId, kind }) =>
      api<LifecycleExecuteResponse>(buildLifecycleExecutePath(kind, userId), {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
  });
}
